import React, { FC, useContext, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Scrollbars } from "react-custom-scrollbars";
import { Range } from "react-input-range";
import isEqual from "lodash/isEqual";
import cn from "clsx";
import { selectedFiltersContext } from ".";
import { useIsRTL } from "@/hooks";
import RangeSelector from "@/components/rangeSelector";
import SquareRadio from "@/components/squareRadio";
import { FilterRangePropsType } from "../types";

const FilterRangeMob: FC<FilterRangePropsType> = ({
  filter,
  setToggledFilter,
  toggledFilter
}) => {
  const filterData = filter?.data;
  const filterCurrency = filterData?.currency;
  const initialRange = filterData?.selectedRange ?? { min: 0, max: 0 };

  const [rangeValue, setRangeValue] = useState(initialRange);
  const [inputMin, setInputMin] = useState(String(initialRange.min));
  const [inputMax, setInputMax] = useState(String(initialRange.max));
  const { onSelection, updateSelectedFilters, selectedFilters } = useContext(
    selectedFiltersContext
  );
  const isRTL = useIsRTL();
  const { t } = useTranslation("productListing");
  const [selectedRadioRange, setSelectedRadioRange] = useState(0);
  const inputDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUserInputRef = useRef(false);

  const rangeData = {
    min: filterData.range.min,
    max: filterData.range.max,
    selectedMin: filterData.selectedRange.min,
    selectedMax: filterData.selectedRange.max
  };

  useEffect(() => {
    const { min, max } = filterData.selectedRange;
    setRangeValue({ min, max });
    setInputMin(String(min));
    setInputMax(String(max));
    setSelectedRadioRange(0);
    hasUserInputRef.current = false;
  }, [filterData.selectedRange.min, filterData.selectedRange.max]);

  useEffect(() => {
    const [minAttr, maxAttr] = filter.queryParam.split("&");
    const [minKey] = minAttr.split("=");
    const [maxKey] = maxAttr.split("=");

    const hasPriceSelection =
      selectedFilters[minKey] || selectedFilters[maxKey];

    if (!hasPriceSelection && Object.keys(selectedFilters).length === 0) {
      setRangeValue({ min: rangeData.min, max: rangeData.max });
      setInputMin(String(rangeData.min));
      setInputMax(String(rangeData.max));
      setSelectedRadioRange(0);
      hasUserInputRef.current = false;
    }
  }, [selectedFilters, rangeData.min, rangeData.max, filter.queryParam]);

  const onRangeComplete = async () => {
    const [minAttr, maxAttr] = filter.queryParam.split("&");
    const [minKey] = minAttr.split("=");
    const [maxKey] = maxAttr.split("=");

    if (!isEqual(filterData?.selectedRange, rangeValue)) {
      await onSelection([
        {
          ...filter,
          queryParam: minKey,
          selectedOptions: [{ key: rangeValue.min }]
        },
        {
          ...filter,
          queryParam: maxKey,
          selectedOptions: [{ key: rangeValue.max }]
        }
      ]);
    }

    setToggledFilter(null);
  };

  const clampValue = (value: number) =>
    Math.min(rangeData.max, Math.max(rangeData.min, value));

  const applyRangeUpdate = (min: number, max: number) => {
    const clampedMin = clampValue(min);
    const clampedMax = clampValue(max);
    const normalizedRange = {
      min: Math.min(clampedMin, clampedMax),
      max: Math.max(clampedMin, clampedMax)
    };

    setRangeValue(normalizedRange);
    setInputMin(String(normalizedRange.min));
    setInputMax(String(normalizedRange.max));
    updatePriceFilters(normalizedRange.min, normalizedRange.max);
  };

  const handleMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, "");
    setSelectedRadioRange(0);
    setInputMin(digitsOnly);
    hasUserInputRef.current = true;
  };

  const handleMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, "");
    setSelectedRadioRange(0);
    setInputMax(digitsOnly);
    hasUserInputRef.current = true;
  };

  const handleMinBlur = () => {
    if (inputMin !== "") return;
    const fallbackMin = rangeValue?.min ?? rangeData.min;
    setInputMin(String(fallbackMin));
    applyRangeUpdate(fallbackMin, rangeValue.max);
    hasUserInputRef.current = true;
  };

  const handleMaxBlur = () => {
    if (inputMax !== "") return;
    const fallbackMax = rangeValue?.max ?? rangeData.max;
    setInputMax(String(fallbackMax));
    applyRangeUpdate(rangeValue.min, fallbackMax);
    hasUserInputRef.current = true;
  };

  useEffect(() => {
    if (inputDebounceRef.current) {
      clearTimeout(inputDebounceRef.current);
    }

    if (!hasUserInputRef.current) return;

    if (inputMin === "" || inputMax === "") return;

    const minValue = Number(inputMin);
    const maxValue = Number(inputMax);

    if (Number.isNaN(minValue) || Number.isNaN(maxValue)) return;

    const normalized = {
      min: Math.min(minValue, maxValue),
      max: Math.max(minValue, maxValue)
    };

    const isWithinRange =
      normalized.min >= rangeData.min && normalized.max <= rangeData.max;

    const applyNormalized = (range: { min: number; max: number }) => {
      setRangeValue(range);
      setInputMin(String(range.min));
      setInputMax(String(range.max));
      updatePriceFilters(range.min, range.max);
    };

    if (isWithinRange) {
      applyNormalized(normalized);
      return;
    }

    inputDebounceRef.current = setTimeout(() => {
      const clamped = {
        min: clampValue(normalized.min),
        max: clampValue(normalized.max)
      };
      const fixed = {
        min: Math.min(clamped.min, clamped.max),
        max: Math.max(clamped.min, clamped.max)
      };
      applyNormalized(fixed);
    }, 1000);

    return () => {
      if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current);
    };
  }, [inputMin, inputMax, rangeData.min, rangeData.max]);

  const onFilterClearClickHandler = () => {
    const [minAttr, maxAttr] = filter.queryParam.split("&");
    const [minKey] = minAttr.split("=");
    const [maxKey] = maxAttr.split("=");

    const needToSendData =
      rangeData.min !== rangeData.selectedMin ||
      rangeData.max !== rangeData.selectedMax;

    if (needToSendData) {
      onSelection([
        {
          queryParam: minKey,
          selectedOptions: []
        },
        {
          queryParam: maxKey,
          selectedOptions: []
        }
      ]);
    }

    setRangeValue({ min: rangeData.min, max: rangeData.max });
    setInputMin(String(rangeData.min));
    setInputMax(String(rangeData.max));
  };

  const onBackButtonClick = e => {
    e.stopPropagation();
    setRangeValue(filterData.selectedRange);
    setInputMin(String(filterData.selectedRange.min));
    setInputMax(String(filterData.selectedRange.max));
    setToggledFilter({});
  };

  const isCurrentToggledFilter =
    toggledFilter && toggledFilter.queryParam === filter.queryParam;

  const labelText = `(${filterCurrency} ${filterData.selectedRange.min}-${filterData.selectedRange.max})`;

  const listOfRanges = [
    {
      key: 1,
      text: "0 AED - 30 AED",
      min: 0,
      max: 30
    },
    {
      key: 2,
      text: "30 AED - 70 AED",
      min: 30,
      max: 70
    },
    {
      key: 3,
      text: "70 AED - 100 AED",
      min: 70,
      max: 100
    },
    {
      key: 4,
      text: "100 AED - 175 AED",
      min: 100,
      max: 175
    },
    {
      key: 5,
      text: "175 AED - 500 AED",
      min: 175,
      max: 500
    },
    {
      key: 6,
      text: "500 AED - 10000 AED",
      min: 500,
      max: 10000
    }
  ];

  const updatePriceFilters = (min: number, max: number) => {
    const [minAttr, maxAttr] = filter.queryParam.split("&");
    const [minKey] = minAttr.split("=");
    const [maxKey] = maxAttr.split("=");
    let newPriceFilters = [];
    newPriceFilters.push(
      {
        ...filter,
        queryParam: minKey,
        selectedOptions: [{ key: min }]
      },
      {
        ...filter,
        queryParam: maxKey,
        selectedOptions: [{ key: max }]
      }
    );
    updateSelectedFilters(newPriceFilters);
  };

  const onRangeOptionClick = range => {
    setSelectedRadioRange(range.key);
    applyRangeUpdate(range.min, range.max);
  };

  const changeRange = ({ min, max }: Range) => {
    setSelectedRadioRange(0);
    applyRangeUpdate(min, max);
  };

  return (
    <div className={"filter_content"} onClick={e => e.stopPropagation()}>
      <div className="filter_content_scroll_host">
        <div className="filter_content_scroll_area">
          <Scrollbars style={{ width: "100%", height: "100%" }}>
            <div className="range_container">
              <div className="range_dragger_container">
                <p className={cn({ arabic: isRTL })}>
                  <span>{t("range")}</span>
                </p>
                <div className="range_value">
                  <label>
                    <span>{filterCurrency}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputMin}
                      onChange={handleMinInputChange}
                      onBlur={handleMinBlur}
                      aria-label={t("range")}
                    />
                  </label>
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "bold",
                      color: "#d9d9d9",
                      margin: "0 5px"
                    }}
                  >
                    &mdash;
                  </span>
                  <label>
                    <span>{filterCurrency}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputMax}
                      onChange={handleMaxInputChange}
                      onBlur={handleMaxBlur}
                      aria-label={t("range")}
                    />
                  </label>
                </div>
                <div className="range_dragger">
                  <RangeSelector
                    minValue={rangeData.min}
                    maxValue={rangeData.max}
                    value={rangeValue}
                    changeRange={changeRange}
                  />
                </div>
              </div>
            </div>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

export default FilterRangeMob;
